import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { useMutation, useQuery } from "@apollo/client";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import EmailPreviewDialog, { EmailPreviewModalActions } from "@/components/EmailPreviewDialog";
import TextField from "@/components/TextField";
import FileUpload from "@/components/FileUpload/FileUpload";
import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import IconButton from "@/components/IconButton";
import ListItem from "@/components/ListItem/ListItem";
import SubSectionHeading from "@/components/SubSectionHeading";
import { useEffect, useRef, useState } from "react";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import classNames from "classnames";
import { createJobApplicationSchema, CreateJobApplicationFormData } from "./schema";
import DynamicQuestionRenderer from "./DynamicQuestionRenderer";
import { JobPost, isExecuSearchServiceLevel } from "../../types";
import { CREATE_JOB_APPLICATION, JOB_POST_WITH_APPLICATIONS_QUERY } from "../../queries";
import Select from "@/components/Select";
import MultiSelect from "@/components/MultiSelect/MultiSelect";
import { ALL_US_STATES_ABBRV } from "@/lib/usStateHelpers";
import { PERSON_CONTACT_AUTOFILL_QUERY } from "@/pages/people/queries";
import { useSomePeopleQuery } from "@/pages/people/_hooks/useSomePeopleQuery";
import { type PersonHit, getPersonDisplayName } from "@/components/AlgoliaSearch";
import { GovernanceRolePersonPicker } from "@/pages/facilities/[facilityId]/people/governance/_GovernanceRolePersonPicker";
import { useFlags } from "@/hooks/useFeatureFlags";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useJobApplicationConfirmationEmailPreview } from "../../_hooks/useJobApplicationConfirmationEmailPreview";
import useAmplitude from "@/hooks/useAmplitude";
import { JOBS_ANALYTICS_EVENTS, getJobsAnalyticsData } from "@/pages/jobs/_utils/amplitude";

interface CreateJobApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  jobPost: JobPost;
  onSuccess?: () => void;
}

function personDataFromAlgoliaHit(hit: PersonHit) {
  const nameParts = (hit.preferred_name || hit.name || "").split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    pgaId: hit.pga_id,
    firstName,
    lastName,
    email: hit.email,
    phoneNumbers: hit.phone ? [{ phoneNumber: hit.phone, phoneType: "Primary" }] : [],
    profilePhoto: hit.profile_photo,
    addresses:
      hit.city && hit.state
        ? [
            {
              city: hit.city,
              state: hit.state,
              addressType: "Primary"
            }
          ]
        : [],
    employments: hit.facility_name
      ? [
          {
            title: "",
            facility: { name: hit.facility_name },
            isPrimary: true
          }
        ]
      : []
  };
}

export default function CreateJobApplicationDialog({
  open,
  onClose,
  jobPost,
  onSuccess
}: CreateJobApplicationDialogProps) {
  const isExecuSearch = isExecuSearchServiceLevel(jobPost.serviceLevel);
  const { trackEvent } = useAmplitude();

  const [resumeError, setResumeError] = useState<string | undefined>(undefined);
  const [coverLetterError, setCoverLetterError] = useState<string | undefined>(undefined);
  const [personError, setPersonError] = useState<string | undefined>(undefined);

  // Tracks successful submission to prevent the "unsaved changes" confirm dialog
  // from firing due to stale isDirty closure when onClosed fires after reset()
  const submittedSuccessfully = useRef(false);

  const flags = useFlags();
  const useAlgoliaPeople = (flags as Record<string, boolean>)["backoffice-algolia-person-index"];
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });

  const [peopleVariables, setPeopleVariables] = useState<{ search?: string }>({});
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [personSearchQuery, setPersonSearchQuery] = useState<string>("");
  const [personSearchKey, setPersonSearchKey] = useState(0);
  const [selectedPersonHit, setSelectedPersonHit] = useState<PersonHit | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useJobApplicationConfirmationEmailPreview();

  const { data: personData, loading: peopleLoading } = useSomePeopleQuery({
    variables: { input: peopleVariables },
    skip: useAlgoliaPeople || !peopleVariables.search || peopleVariables.search.trim() === ""
  });

  const selectedPgaId = selectedPerson?.pgaId;
  const { data: contactAutofillData } = useQuery(PERSON_CONTACT_AUTOFILL_QUERY, {
    variables: { pgaId: selectedPgaId ?? "" },
    skip: !open || !selectedPgaId || !canViewPersonContact,
    fetchPolicy: "network-only"
  });

  const methods = useForm<CreateJobApplicationFormData>({
    resolver: zodResolver(createJobApplicationSchema(isExecuSearch)),
    defaultValues: {
      pgaId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      website: "",
      city: "",
      state: "",
      currentOrMostRecentPosition: "",
      currentOrMostRecentEmployer: "",
      referralSource: [],
      resume: undefined,
      coverLetter: undefined,
      customAnswers: []
    }
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    getValues,
    watch,
    resetField
  } = methods;

  const emailValue = watch("email");
  const canPreviewEmail = Boolean(
    selectedPerson && (emailValue?.trim() || selectedPerson.pgaId || selectedPerson.email)
  );

  const referralSourceOptions =
    jobPost.applicationForm?.referralSourceOptions?.map((source: string) => ({
      value: source,
      label: source
    })) ?? [];

  const [createJobApplication, { loading }] = useMutation(CREATE_JOB_APPLICATION, {
    refetchQueries: [
      {
        query: JOB_POST_WITH_APPLICATIONS_QUERY,
        variables: { jobPostId: jobPost.id }
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data?.createJobApplication?.success) {
        trackEvent(
          JOBS_ANALYTICS_EVENTS.applicationCreated,
          getJobsAnalyticsData({
            source: "jobs_application_create",
            jobPostId: jobPost.id,
            action: "create_application"
          })
        );
        showSnackbar("Job application created", { close: true });
        submittedSuccessfully.current = true;
        reset();
        onSuccess?.();
        onClose();
      } else {
        const message = data?.createJobApplication?.message;
        if (message) {
          showErrorSnackbar("Application not created", { subtitle: message });
        } else {
          showErrorSnackbar("Application not created", { subtitle: "Please try again later." });
        }
      }
    },
    onError: () => {
      showErrorSnackbar("Application not created", { subtitle: "Please try again later." });
    }
  });

  useEffect(() => {
    if (useAlgoliaPeople) return;

    const handler = setTimeout(() => {
      setPeopleVariables({ search: debouncedSearchTerm });
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm, useAlgoliaPeople]);

  useEffect(() => {
    if (open) {
      reset();
      setResumeError(undefined);
      setCoverLetterError(undefined);
      setPersonError(undefined);
      setPersonSearchQuery("");
      setDebouncedSearchTerm("");
      setPeopleVariables({});
      setSelectedPerson(null);
      setSelectedPersonHit(null);
      setPersonSearchKey((key) => key + 1);
    }
  }, [open, reset]);

  const populateFromPerson = (person: any) => {
    setSelectedPerson(person);
    setPersonError(undefined);

    if (person.email != null && person.email !== "") {
      setValue("email", person.email);
    }
    const primaryOrFirstPhone =
      person.phoneNumbers?.find((p: any) => p.phoneType === "Primary")?.phoneNumber ||
      person.phoneNumbers?.[0]?.phoneNumber;
    if (primaryOrFirstPhone) {
      setValue("phone", primaryOrFirstPhone);
    }

    if (isExecuSearch) {
      const primaryAddress = person.addresses?.[0];
      if (primaryAddress?.city) {
        setValue("city", primaryAddress.city);
      }
      if (primaryAddress?.state) {
        setValue("state", primaryAddress.state);
      }

      const primaryEmployment = person.employments?.find((e: any) => e.isPrimary) || person.employments?.[0];
      if (primaryEmployment?.facility?.name) {
        setValue("currentOrMostRecentEmployer", primaryEmployment.facility.name);
      }
    }
  };

  useEffect(() => {
    const contact = contactAutofillData?.person;
    if (!contact || !selectedPgaId) return;

    setSelectedPerson((prev: any) =>
      prev
        ? {
            ...prev,
            email: contact.email ?? prev.email,
            phoneNumbers: contact.phoneNumbers?.length ? contact.phoneNumbers : prev.phoneNumbers,
            addresses: contact.addresses?.length ? contact.addresses : prev.addresses
          }
        : prev
    );

    setSelectedPersonHit((prev) => {
      if (!prev) return prev;

      const primaryOrFirstPhone =
        contact.phoneNumbers?.find((p) => p.phoneType === "Primary")?.phoneNumber ||
        contact.phoneNumbers?.[0]?.phoneNumber;
      const primaryAddress = contact.addresses?.[0];

      return {
        ...prev,
        email: contact.email ?? prev.email,
        phone: primaryOrFirstPhone ?? prev.phone,
        city: primaryAddress?.city ?? prev.city,
        state: primaryAddress?.state ?? prev.state
      };
    });

    if (!getValues("email") && contact.email) {
      setValue("email", contact.email);
    }
    const primaryOrFirstPhone =
      contact.phoneNumbers?.find((p) => p.phoneType === "Primary")?.phoneNumber ||
      contact.phoneNumbers?.[0]?.phoneNumber;
    if (!getValues("phone") && primaryOrFirstPhone) {
      setValue("phone", primaryOrFirstPhone);
    }
    if (isExecuSearch) {
      const primaryAddress = contact.addresses?.[0];
      if (!getValues("city") && primaryAddress?.city) {
        setValue("city", primaryAddress.city);
      }
      if (!getValues("state") && primaryAddress?.state) {
        setValue("state", primaryAddress.state);
      }
    }
  }, [contactAutofillData, getValues, isExecuSearch, selectedPgaId, setValue]);

  const onSubmit = async (formData: CreateJobApplicationFormData) => {
    if (!selectedPerson) {
      setPersonError("Please select a person from the search");
      return;
    }

    setPersonError(undefined);

    const attachments = [];
    if (formData.resume) {
      attachments.push({ type: "resume" as const, file: formData.resume });
    }
    if (formData.coverLetter) {
      attachments.push({ type: "cover_letter" as const, file: formData.coverLetter });
    }

    const answers = formData.customAnswers
      ?.map((answer) => ({
        questionId: answer.questionId,
        optionIds: answer.selectedOptionId
          ? [answer.selectedOptionId]
          : answer.selectedOptionIds?.length
            ? answer.selectedOptionIds
            : undefined,
        customAnswer: answer.textAnswer || undefined
      }))
      .filter((a) => a.optionIds || a.customAnswer);

    const emailFromForm = formData.email?.trim() || undefined;
    const phoneFromForm = formData.phone?.trim() || undefined;
    const emailAddress = emailFromForm || selectedPerson.email || undefined;
    const phoneNumber =
      phoneFromForm ||
      selectedPerson.phoneNumbers?.find((p: any) => p.phoneType === "Primary")?.phoneNumber ||
      selectedPerson.phoneNumbers?.[0]?.phoneNumber ||
      undefined;

    await createJobApplication({
      variables: {
        input: {
          jobPostId: jobPost.id,
          emailAddress,
          phoneNumber,
          website: formData.website || undefined,
          pgaId: selectedPerson.pgaId || undefined,
          attachments: attachments.length > 0 ? attachments : undefined,
          answers: answers && answers.length > 0 ? answers : undefined,
          ...(isExecuSearch && {
            city: formData.city,
            state: formData.state,
            currentOrMostRecentPosition: formData.currentOrMostRecentPosition,
            currentOrMostRecentEmployer: formData.currentOrMostRecentEmployer,
            referralSource: formData.referralSource
          })
        }
      }
    });
  };

  useEffect(() => {
    if (!open) {
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview]);

  const handlePreviewEmail = () => {
    if (!selectedPerson) {
      setPersonError("Please select a person from the search");
      return;
    }

    const emailAddress = getValues("email")?.trim() || selectedPerson.email || undefined;
    if (!emailAddress && !selectedPerson.pgaId) {
      setPersonError("Applicant email or PGA ID is required to preview the confirmation email");
      return;
    }

    setPreviewOpen(true);
    void loadPreview({
      jobPostId: jobPost.id,
      pgaId: selectedPerson.pgaId || undefined,
      emailAddress
    });
  };

  const handleClose = () => {
    if (
      !submittedSuccessfully.current &&
      isDirty &&
      !window.confirm("You have unsaved changes. Are you sure you want to close?")
    ) {
      return;
    }
    submittedSuccessfully.current = false;
    reset();
    setResumeError(undefined);
    setCoverLetterError(undefined);
    setPersonError(undefined);
    setSelectedPerson(null);
    setSelectedPersonHit(null);
    setPersonSearchQuery("");
    onClose();
  };

  const clearSelectedPerson = () => {
    setSelectedPerson(null);
    setSelectedPersonHit(null);
    setPersonSearchQuery("");
    setDebouncedSearchTerm("");
    setPeopleVariables({});
    setPersonError(undefined);
    setPersonSearchKey((key) => key + 1);
    setValue("email", "");
    setValue("phone", "");
    if (isExecuSearch) {
      setValue("city", "");
      setValue("state", "");
      setValue("currentOrMostRecentEmployer", "");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClosed={handleClose}
        headline="Add Job Application"
        className="w-[800px] max-w-[90vw] max-h-[85vh]"
        hasFormElements
        actions={
          <EmailPreviewModalActions
            onPreview={handlePreviewEmail}
            previewDisabled={isSubmitting || loading || !canPreviewEmail}
          >
            <Button variant="text" onClick={handleClose} disabled={isSubmitting || loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              iconClassName={classNames((isSubmitting || loading) && "animate-spin w-6 h-6 !block")}
              icon={isSubmitting || loading ? "progress_activity" : undefined}
              onClick={() => {
                if (!selectedPerson) {
                  setPersonError("Please select a person from the search");
                }
                handleSubmit(onSubmit)();
              }}
              disabled={isSubmitting || loading}
            >
              Create Application
            </Button>
          </EmailPreviewModalActions>
        }
      >
        <FormProvider {...methods}>
          <form className="flex flex-col gap-6">
            <div className="flex flex-col gap-6">
              <SubSectionHeading>Person Information</SubSectionHeading>

              {useAlgoliaPeople ? (
                <GovernanceRolePersonPicker
                  key={personSearchKey}
                  label="Search for a Person"
                  showContactDetails
                  selectedPerson={selectedPersonHit}
                  error={!!personError}
                  errorText={personError}
                  disabled={isSubmitting || loading}
                  onSelect={(hit) => {
                    setSelectedPersonHit(hit);
                    setPersonSearchQuery(getPersonDisplayName(hit));
                    populateFromPerson(personDataFromAlgoliaHit(hit));
                  }}
                  onClear={clearSelectedPerson}
                />
              ) : (
                <>
                  {!selectedPerson && (
                    <Autocomplete
                      label="Search for a Person"
                      query={personSearchQuery}
                      loadingResults={peopleLoading}
                      required
                      error={!!personError}
                      errorText={personError}
                      data={
                        (personData?.people?.nodes || []) as Array<{
                          id: string;
                          firstName: string;
                          lastName: string;
                          pgaId: string;
                          primarySectionAffiliation: { section: { name: string } };
                          primaryFacility: { name: string };
                          profilePhoto: string;
                        }>
                      }
                      onChangeText={(searchText) => {
                        setPersonSearchQuery(searchText);
                        setDebouncedSearchTerm(searchText);
                        if (searchText === "") {
                          setSelectedPerson(null);
                        }
                      }}
                      reset={() => {
                        setPersonSearchQuery("");
                        setPeopleVariables({ search: "" });
                        setSelectedPerson(null);
                      }}
                      onSelect={(selected) => {
                        setPersonSearchQuery(`${selected.firstName} ${selected.lastName}`);
                        populateFromPerson(selected);
                      }}
                      renderItem={(data: {
                        id: string;
                        firstName: string;
                        lastName: string;
                        pgaId: string;
                        primarySectionAffiliation: { section: { name: string } };
                        primaryFacility: { name: string };
                        profilePhoto: string;
                      }) => (
                        <AvatarDetails
                          title={`${data.firstName} ${data.lastName}`}
                          text={data.primaryFacility?.name || ""}
                          picture={data.profilePhoto}
                          secondaryText={data.primarySectionAffiliation?.section?.name || ""}
                        />
                      )}
                    />
                  )}
                  {selectedPerson && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <ListItem
                          compact
                          useAvatar
                          picture={selectedPerson.profilePhoto}
                          headline={`${selectedPerson.firstName} ${selectedPerson.lastName}`}
                          supportingText={
                            [
                              selectedPerson.pgaId
                                ? `PGA ID: ${selectedPerson.pgaId}${selectedPerson.email ? ` • ${selectedPerson.email}` : ""}`
                                : selectedPerson.email,
                              selectedPerson.primarySectionAffiliation?.section?.name,
                              selectedPerson.primaryFacility?.name
                            ]
                              .filter(Boolean)
                              .join("\n") || undefined
                          }
                        />
                      </div>
                      <IconButton
                        name="close"
                        ariaLabel="Remove selected person"
                        title="Clear selected person"
                        onClick={clearSelectedPerson}
                        disabled={isSubmitting || loading}
                        skipMargin
                      />
                    </div>
                  )}
                </>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Email"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.email}
                      errorText={errors.email?.message}
                      className="min-w-0"
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Phone"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.phone}
                      errorText={errors.phone?.message}
                      className="min-w-0"
                    />
                  )}
                />
              </div>

              {!isExecuSearch && (
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Website (Optional)"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.website}
                      errorText={errors.website?.message}
                    />
                  )}
                />
              )}
            </div>

            {isExecuSearch && (
              <div className="flex flex-col gap-6">
                <SubSectionHeading>Application Details</SubSectionHeading>
                <div className="grid grid-cols-[2fr_1fr] gap-6">
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="City"
                        value={field.value}
                        onChange={field.onChange}
                        required
                        error={!!errors.city}
                        errorText={errors.city?.message}
                        className="min-w-0"
                      />
                    )}
                  />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <Select
                        label="State"
                        value={field.value}
                        onSelect={field.onChange}
                        options={ALL_US_STATES_ABBRV.map((s) => ({ value: s, label: s }))}
                        required
                        error={!!errors.state}
                        errorText={errors.state?.message}
                        className="min-w-0"
                      />
                    )}
                  />
                </div>
                <Controller
                  name="currentOrMostRecentPosition"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      className="flex-1"
                      label="Current or Most Recent Position"
                      value={field.value}
                      onChange={field.onChange}
                      required
                      error={!!errors.currentOrMostRecentPosition}
                      errorText={errors.currentOrMostRecentPosition?.message}
                    />
                  )}
                />
                <Controller
                  name="currentOrMostRecentEmployer"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      className="flex-1"
                      label="Current or Most Recent Employer"
                      value={field.value}
                      onChange={field.onChange}
                      required
                      error={!!errors.currentOrMostRecentEmployer}
                      errorText={errors.currentOrMostRecentEmployer?.message}
                    />
                  )}
                />
                <div className="grid grid-cols-[2fr_1fr] gap-6">
                  <Controller
                    name="referralSource"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        label="How did you hear about this job?"
                        options={referralSourceOptions}
                        defaultLabel="Select referral source(s)"
                        selectedValues={field.value || []}
                        onSelect={(value, checked) => {
                          const current = field.value || [];
                          field.onChange(checked ? [...current, value] : current.filter((v: string) => v !== value));
                        }}
                        onClear={() => field.onChange([])}
                        skipShowAll
                        required
                        error={!!errors.referralSource}
                        errorText={errors.referralSource?.message}
                      />
                    )}
                  />
                  <Controller
                    name="website"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Website (Optional)"
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.website}
                        errorText={errors.website?.message}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-6">
              <SubSectionHeading>Documents</SubSectionHeading>
              {isExecuSearch ? (
                <>
                  <div className="text-body-medium">
                    Please combine your cover letter, resume and other documents into one PDF file.
                  </div>
                  <FileUpload
                    label="Application Materials (PDF)*"
                    subtitle="Please upload a PDF file."
                    accept=".pdf"
                    maxSizeMB={5}
                    handleUpload={(file) => {
                      setValue("resume", file, { shouldValidate: true });
                      setResumeError(undefined);
                    }}
                    errorMsg={resumeError || errors.resume?.message}
                    onError={setResumeError}
                    onClear={() => {
                      resetField("resume");
                      setResumeError(undefined);
                    }}
                  />
                </>
              ) : (
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex-1">
                    <FileUpload
                      label="Resume*"
                      accept=".pdf"
                      maxSizeMB={5}
                      handleUpload={(file) => {
                        setValue("resume", file, { shouldValidate: true });
                        setResumeError(undefined);
                      }}
                      errorMsg={resumeError || errors.resume?.message}
                      onError={setResumeError}
                      onClear={() => {
                        resetField("resume");
                        setResumeError(undefined);
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <FileUpload
                      label="Cover Letter"
                      accept=".pdf"
                      maxSizeMB={5}
                      handleUpload={(file) => {
                        setValue("coverLetter", file, { shouldValidate: true });
                        setCoverLetterError(undefined);
                      }}
                      errorMsg={coverLetterError || errors.coverLetter?.message}
                      onError={setCoverLetterError}
                      onClear={() => {
                        resetField("coverLetter");
                        setCoverLetterError(undefined);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {jobPost.applicationForm?.questions && jobPost.applicationForm.questions.length > 0 && (
              <div>
                <DynamicQuestionRenderer questions={jobPost.applicationForm.questions} />
              </div>
            )}

            {errors.root && <p className="text-sys-brand-error text-body-medium">{errors.root.message}</p>}
          </form>
        </FormProvider>
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
